import { resolveUrl,axiosInstance,isAxiosError } from '@/lib/api-client'
import { authManager } from '@/lib/auth'
import { useEffect } from 'react'

const LOCK_NAME = 'rp_auth_refresh' as const

axiosInstance.defaults.withCredentials = true

const refreshInstance = axiosInstance.create({ timeout: 15_000, withCredentials: true })

let refreshPromise: Promise<string> | null = null
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (err: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) p.reject(error)
    else p.resolve(token!)
  })
  failedQueue = []
}

// Fresh-from-storage read (authManager reads localStorage on every call), so
// cross-tab refreshes are visible without an in-memory cache going stale.
function getStoredTokenVersion(): string | null {
  return authManager.getAccessToken()
}

async function performTokenRefresh(): Promise<string> {
  const res = await refreshInstance.post(
    resolveUrl(`/auth/refresh`),
    null,
    { withCredentials: true, headers: { 'x-skip-auth-redirect': '1' } },
  )

  const resData = res.data?.data ?? res.data
  const newAccessToken = resData?.accessToken ?? resData?.token

  if (!newAccessToken) throw new Error('Refresh response missing access token')

  // setAccessToken refuses tokens minted for a different account (the refresh
  // cookie is browser-wide and follows the LAST login on this browser). Treat
  // that as a dead session: plain Error → the response interceptor calls
  // handleRefreshFailure(), logging the user out to re-authenticate.
  if (!authManager.setAccessToken(newAccessToken as string)) {
    throw new Error('Signed-in account changed in another tab — please sign in again.')
  }
  return newAccessToken as string
}

async function crossTabRefresh(): Promise<string> {
  const tokenBefore = getStoredTokenVersion()

  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return navigator.locks.request(LOCK_NAME, async () => {
      const tokenAfter = getStoredTokenVersion()
      if (tokenAfter && tokenAfter !== tokenBefore) {
        return tokenAfter
      }
      return performTokenRefresh()
    })
  }

  return performTokenRefresh()
}

function enqueueRefresh(): Promise<string> {
  if (refreshPromise) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject })
    })
  }

  refreshPromise = crossTabRefresh()
    .then((newToken) => {
      processQueue(null, newToken)
      return newToken
    })
    .catch((refreshError: unknown) => {
      processQueue(refreshError, null)
      throw refreshError
    })
    .finally(() => {
      refreshPromise = null
    })

  return refreshPromise
}

export function emitSessionExpired() {
  window.dispatchEvent(new CustomEvent('rp:session-expired'))
}

function handleRefreshFailure() {
  authManager.forceLogout()
  emitSessionExpired()
}

axiosInstance.interceptors.request.use(
  async (config) => {
    const url = config.url ?? ''

    if (
      url.includes('/auth/login') ||
      url.includes('/auth/refresh') ||
      url.includes('.workers.dev')
    ) {
      return config
    }

    const token = authManager.getAccessToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error),
)

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config
    const status = error.response?.status

    // Rate limited: retry a read once after the server's Retry-After (capped so a
    // screen never hangs). Writes are not retried; the API's message tells the
    // cashier how long to wait.
    if (status === 429 && originalRequest && !originalRequest._retry429 && (originalRequest.method ?? 'get') === 'get') {
      originalRequest._retry429 = true
      const retryAfterSec = Number(error.response?.headers?.['retry-after']) || 1
      await new Promise((resolve) => setTimeout(resolve, Math.min(retryAfterSec, 5) * 1000))
      return axiosInstance(originalRequest)
    }

    if (status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error)
    }

    const requestUrl = originalRequest.url ?? ''
    if (requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh')) {
      return Promise.reject(error)
    }

    originalRequest._retry = true

    try {
      const newToken = await enqueueRefresh()
      originalRequest.headers.Authorization = `Bearer ${newToken}`
      return axiosInstance(originalRequest)
    } catch (refreshError: unknown) {
      const isOfflineOrTimeout = isAxiosError(refreshError) && !refreshError.response
      if (!isOfflineOrTimeout) {
        handleRefreshFailure()
      }
      return Promise.reject(refreshError)
    }
  },
)

export function AxiosGlobalConfig() {
  useEffect(() => {
    if (!authManager.isAuthenticated()) return

    const intervalId = setInterval(() => {
      if (!authManager.isAuthenticated()) return

      if (authManager.isTokenExpiringSoon() && !refreshPromise) {
        enqueueRefresh().catch(() => {})
      }
    }, 60_000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  return null
}
